import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import { Form, Loader, Message, Modal } from 'semantic-ui-react'
import { FlexRowBetween, LoaderBar, TitleText } from 'components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import { useNotification } from 'hooks/toast'
import { useAuth } from 'context'
import { SectionWorkflowService } from 'utility'
import {
    PORTAL_SECTION_NAME,
    SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID,
} from './customer-claim-inspection-utils'
import {
    EnvelopeSimple,
    User,
    X,
    Check,
} from 'phosphor-react'
import {
    collectAssignedUserIds,
    companyUsersFromAssignments,
    companyUsersFromRecipientsMap,
    dedupeCompanyUsers,
    extractWorkflowErrorMessage,
    isDuplicateRecipient,
    normalizeNotificationRecipient,
    normalizeNotificationRecipients,
    normalizeUserSearchResults,
    normalizeWorkflowAssignments,
    toNotificationRecipientPayload,
    toWorkflowAssignmentPayload,
    unwrapRecipientsResponse,
    unwrapUserSearchResponse,
    unwrapWorkflowResponse,
    USER_SEARCH_DEFAULT_LIMIT,
    USER_SEARCH_MAX_LIMIT,
    validatePhaseSetup,
} from './notification-recipients-utils'

const PageWrap = styled.div`
    margin: -1rem;
    min-height: calc(100vh - 55px);
    background: #f1f5f9;
    font-family: inherit;
    overflow: visible;
`

const Content = styled.div`
    padding: 1.5rem 1.75rem 5rem;
    max-width: 1180px;
    margin: 0 auto;
    overflow: visible;
`

const Crumb = styled.div`
    font-size: 0.75rem;
    color: #64748b;
    margin-bottom: 0.5rem;

    .small {
        font-size: 0.7rem;
    }
`

const PageTitle = styled.h1`
    margin: 0 0 0.35rem;
    font-size: 1.375rem;
    font-weight: 700;
    color: #0f172a;
`

const PageSub = styled.p`
    margin: 0 0 1.125rem;
    color: #64748b;
    font-size: 0.84rem;
    line-height: 1.5;
`

const PhaseCard = styled.section`
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 1px 4px rgba(26, 43, 75, 0.08);
    margin-bottom: 1rem;
    overflow: visible;
`

const SimplePhaseHeader = styled.div`
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
    padding: 0.65rem 1rem;
    min-height: 2.75rem;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    font-weight: 700;
    font-size: 0.95rem;
    border-radius: 8px 8px 0 0;
`

const CardBody = styled.div`
    padding: 1rem 1.25rem;
    overflow: visible;
`

const PhaseHint = styled.p`
    margin: 0 0 1rem;
    font-size: 0.85rem;
    color: #1a2b4b;
    line-height: 1.45;
`

const RecipientSection = styled.div`
    padding: 1rem 1.25rem;
    background: #f8fafc;
    border-top: 1px solid #e2e8f0;
`

const HintText = styled.span`
    display: block;
    font-size: 0.75rem;
    color: #767676;
    margin-top: 0.25rem;
    line-height: 1.35;
`

/** Hides blinking caret when a value is selected and the menu is closed. */
const AssignedUserField = styled.div`
    margin-bottom: 0.5rem;
`

const UserMatchList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
    margin-top: 0.5rem;
    max-height: 240px;
    overflow-y: auto;
    align-content: flex-start;
`

const UserMatchItem = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    max-width: 100%;
    text-align: left;
    padding: 0.35rem 0.65rem;
    border: 1px solid #67e8f9;
    border-radius: 999px;
    background: #ecfeff;
    color: #155e75;
    font-size: 0.78rem;
    line-height: 1.3;
    font-weight: 600;
    cursor: pointer;

    .chip-meta {
        font-weight: 500;
        color: #64748b;
        font-size: 0.72rem;
    }

    .chip-selected {
        flex-shrink: 0;
        margin-left: 0.1rem;
        color: ${({ theme }) => theme.colors.primary};
    }

    &:hover {
        background: #cffafe;
    }

    &.selected {
        border-color: ${({ theme }) => theme.colors.primary};
        box-shadow: 0 0 0 1px ${({ theme }) => theme.colors.primary};
        background: #dbeafe;
    }
`

const ChipArea = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem;
`

const AssignedChipArea = styled(ChipArea)`
    margin-top: 0.35rem;
    margin-bottom: 0.5rem;
`

const RecipientChipArea = styled(ChipArea)`
    margin-top: 0.75rem;
    margin-bottom: 0.85rem;
`

const SubjectHint = styled(HintText)`
    margin-top: 0.35rem;
    margin-bottom: 0;
`

const RecipientChip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.25rem 0.55rem;
    border-radius: 12px;
    font-size: 0.75rem;
    line-height: 1.3;
    border: 1px solid ${({ $external }) => ($external ? '#fcd34d' : '#67e8f9')};
    background: ${({ $external }) => ($external ? '#fef3c7' : '#ecfeff')};
    color: ${({ $external }) => ($external ? '#78350f' : '#155e75')};

    .chip-meta {
        color: ${({ $external }) => ($external ? '#92400e' : '#64748b')};
        font-size: 0.68rem;
    }

    .chip-remove {
        margin-left: 0.1rem;
        cursor: pointer;
        opacity: 0.55;
        display: inline-flex;
        align-items: center;
        border: none;
        background: transparent;
        padding: 0;
        color: inherit;

        &:hover {
            opacity: 1;
            color: #dc2626;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.35;
        }
    }
`

const ActionRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
`

const AssignedActionRow = styled(ActionRow)`
    margin-top: 0.35rem;
`

const RecipientActionRow = styled(ActionRow)`
    margin-top: ${({ $hasRecipients }) =>
        $hasRecipients ? '0' : '0.75rem'};
`

const SaveBar = styled.div`
    position: sticky;
    bottom: 0;
    margin-top: 1.125rem;
    padding: 0.75rem 0.875rem;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    gap: 0.875rem;
    box-shadow: 0 -4px 8px rgba(15, 23, 42, 0.06);
    z-index: 5;
`

const SaveBarMsg = styled.span`
    color: #64748b;
    font-size: 0.78rem;
    flex: 1;
`

const DirtyDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #d97706;
    display: inline-block;
    margin-right: 0.35rem;
    vertical-align: middle;
`

const SaveBarActions = styled.div`
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
`

const ModalModeRow = styled.div`
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1rem;

    .ui.button {
        flex: 1;
        display: inline-flex !important;
        align-items: center;
        justify-content: center;
        gap: 0.35rem;
        margin: 0 !important;
    }
`

const SearchStatusRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-height: 1.25rem;
    margin-top: 0.35rem;
    font-size: 0.75rem;
    color: #94a3b8;
    font-style: italic;
`

const clonePhases = phases =>
    (phases || []).map(phase => ({ ...phase }))

const cloneRecipientsMap = map => {
    const next = {}
    Object.entries(map || {}).forEach(([sectionId, rows]) => {
        next[sectionId] = (rows || []).map(row => ({ ...row }))
    })
    return next
}

const resolveAssignedUsersForPhase = (phase, companyUsers) => {
    const ids = phase?.assignedUserIds || []
    const lookup = new Map();
    [
        ...(phase?.assignedUsers || []),
        ...(companyUsers || []).filter(
            user => user?.id && ids.includes(user.id),
        ),
    ].forEach(user => {
        if (user?.id) lookup.set(user.id, user)
    })
    return ids.map(id => lookup.get(id) || { id, fullName: '', email: '' })
}

const formatAssignedUserLabel = user => {
    if (user?.fullName) return user.fullName
    if (user?.email) return user.email
    return `ID ${user.id}`
}

const renderAssignedUserChip = (user, onRemove, disabled) => (
    <RecipientChip key={`assigned-${user.id}`}>
        <User size={14} weight="bold" />
        {formatAssignedUserLabel(user)}
        {user.fullName && user.email ? (
            <span className="chip-meta"> · {user.email}</span>
        ) : null}
        <button
            type="button"
            className="chip-remove"
            title="Remove assigned user"
            disabled={disabled}
            onClick={() => onRemove(user.id)}
        >
            <X size={14} weight="bold" />
        </button>
    </RecipientChip>
)

const enrichPhasesWithAssignedUsers = (assignments, companyUsers) =>
    (assignments || []).map(phase => {
        const assignedUsers = (phase.assignedUserIds || []).map(
            userId =>
                companyUsers.find(user => user.id === userId) ||
                (phase.assignedUsers || []).find(user => user.id === userId) || {
                    id: userId,
                    fullName: '',
                    email: '',
                },
        )
        return { ...phase, assignedUsers }
    })

const renderRecipientChip = (recipient, onRemove, disabled) => {
    const isExternal = !recipient.internalUserId
    const label = isExternal
        ? recipient.externalName || recipient.externalEmail
        : recipient.internalUserName || 'User'
    const meta = isExternal
        ? recipient.externalEmail
        : recipient.internalUserEmail

    return (
        <RecipientChip key={recipient.key} $external={isExternal}>
            {isExternal ? (
                <EnvelopeSimple size={14} weight="bold" />
            ) : (
                <User size={14} weight="bold" />
            )}
            {label}
            {meta ? <span className="chip-meta"> · {meta}</span> : null}
            <button
                type="button"
                className="chip-remove"
                title="Remove recipient"
                disabled={disabled}
                onClick={() => onRemove(recipient.key)}
            >
                <X size={14} weight="bold" />
            </button>
        </RecipientChip>
    )
}

export const NotificationRecipientsPage = () => {
    const { user } = useAuth()
    const { successToast, errorToast } = useNotification()
    const companyId = user?.CompanyId ?? user?.Companies?.[0]?.CompanyId

    const [phases, setPhases] = useState([])
    const [savedPhases, setSavedPhases] = useState([])
    const [recipientsBySection, setRecipientsBySection] = useState({})
    const [savedRecipientsBySection, setSavedRecipientsBySection] = useState({})
    const [companyUsers, setCompanyUsers] = useState([])
    const pinnedAssignedUsersRef = useRef([])
    const [loading, setLoading] = useState(true)
    const [accessDenied, setAccessDenied] = useState(false)
    const [dirty, setDirty] = useState(false)
    const [saving, setSaving] = useState(false)

    const [modalOpen, setModalOpen] = useState(false)
    const [modalType, setModalType] = useState('recipient')
    const [modalSection, setModalSection] = useState(null)
    const [recMode, setRecMode] = useState('internal')
    const [userSearch, setUserSearch] = useState('')
    const [userMatches, setUserMatches] = useState([])
    const [userSearchLoading, setUserSearchLoading] = useState(false)
    const [pendingUserId, setPendingUserId] = useState(null)
    const [pendingUserIds, setPendingUserIds] = useState([])
    const [externalName, setExternalName] = useState('')
    const [externalEmail, setExternalEmail] = useState('')

    const searchTimerRef = useRef(null)
    const loadStartedRef = useRef(false)
    const recipientKeyRef = useRef(1)
    const errorToastRef = useRef(errorToast)
    const successToastRef = useRef(successToast)

    useEffect(() => {
        errorToastRef.current = errorToast
        successToastRef.current = successToast
    }, [errorToast, successToast])

    const attachRecipientKeys = useCallback((sectionId, rows) => {
        return (rows || []).map((row, index) => ({
            ...row,
            key: row.id
                ? `api-${sectionId}-${row.id}`
                : `local-${sectionId}-${index}`,
        }))
    }, [])

    const fetchCompanyUsers = useCallback(
        async (q = '') => {
            const rows = await SectionWorkflowService.searchUsers({
                q: String(q ?? ''),
                companyId,
                limit: USER_SEARCH_DEFAULT_LIMIT,
            })
            const normalized = normalizeUserSearchResults(
                unwrapUserSearchResponse(rows),
            )
            const merged = dedupeCompanyUsers([
                ...pinnedAssignedUsersRef.current,
                ...normalized,
            ])
            setCompanyUsers(merged)
            return merged
        },
        [companyId],
    )

    const resolveDropdownUsers = useCallback(
        async (assignments, seedUsers, recipientsMap) => {
            let merged = dedupeCompanyUsers([
                ...seedUsers,
                ...companyUsersFromRecipientsMap(recipientsMap),
                ...companyUsersFromAssignments(assignments),
            ])

            const missingIds = collectAssignedUserIds(assignments).filter(
                userId => !merged.some(user => user.id === userId),
            )

            if (missingIds.length) {
                const bulkResponse =
                    await SectionWorkflowService.searchUsers({
                        q: '',
                        companyId,
                        limit: USER_SEARCH_MAX_LIMIT,
                    })
                merged = dedupeCompanyUsers([
                    ...merged,
                    ...normalizeUserSearchResults(
                        unwrapUserSearchResponse(bulkResponse),
                    ),
                ])
            }

            const assignedIds = collectAssignedUserIds(assignments)
            pinnedAssignedUsersRef.current = merged.filter(user =>
                assignedIds.includes(user.id),
            )
            return merged
        },
        [companyId],
    )

    const loadPage = useCallback(async () => {
        setLoading(true)
        setAccessDenied(false)
        try {
            const workflowResponse = await SectionWorkflowService.getWorkflow(
                SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID,
            )
            const assignments = normalizeWorkflowAssignments(
                unwrapWorkflowResponse(workflowResponse),
            )

            const nextRecipients = {}
            await Promise.all(
                assignments.map(async phase => {
                    try {
                        const rows =
                            await SectionWorkflowService.getNotificationRecipients(
                                SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID,
                                phase.sectionId,
                            )
                        nextRecipients[phase.sectionId] = attachRecipientKeys(
                            phase.sectionId,
                            normalizeNotificationRecipients(
                                unwrapRecipientsResponse(rows),
                            ),
                        )
                    } catch (err) {
                        nextRecipients[phase.sectionId] = []
                        if (err?.status !== 400) {
                            throw err
                        }
                    }
                }),
            )

            let normalizedUsers = []
            try {
                normalizedUsers = await fetchCompanyUsers('')
            } catch {
                // optional seed
            }

            const dropdownUsers = await resolveDropdownUsers(
                assignments,
                normalizedUsers,
                nextRecipients,
            )
            setCompanyUsers(dropdownUsers)

            const enrichedPhases = enrichPhasesWithAssignedUsers(
                assignments,
                dropdownUsers,
            )
            setPhases(clonePhases(enrichedPhases))
            setSavedPhases(clonePhases(enrichedPhases))
            setRecipientsBySection(cloneRecipientsMap(nextRecipients))
            setSavedRecipientsBySection(cloneRecipientsMap(nextRecipients))
            setDirty(false)
        } catch (err) {
            if (err?.status === 403) {
                setAccessDenied(true)
            } else {
                errorToastRef.current(extractWorkflowErrorMessage(err))
            }
            setPhases([])
            setSavedPhases([])
            setRecipientsBySection({})
            setSavedRecipientsBySection({})
        } finally {
            setLoading(false)
        }
    }, [attachRecipientKeys, fetchCompanyUsers, resolveDropdownUsers])

    useEffect(() => {
        if (loadStartedRef.current) return
        loadStartedRef.current = true
        loadPage()
    }, [loadPage])

    const sortedPhases = useMemo(
        () => [...phases].sort((a, b) => a.phaseOrder - b.phaseOrder),
        [phases],
    )

    const updatePhase = (sectionId, patch) => {
        setPhases(prev =>
            prev.map(phase =>
                phase.sectionId === sectionId ? { ...phase, ...patch } : phase,
            ),
        )
        setDirty(true)
    }

    const handleRemoveRecipient = (sectionId, recipientKey) => {
        setRecipientsBySection(prev => ({
            ...prev,
            [sectionId]: (prev[sectionId] || []).filter(
                item => item.key !== recipientKey,
            ),
        }))
        setDirty(true)
    }

    const handleRemoveAssignedUser = (sectionId, userId) => {
        setPhases(prev =>
            prev.map(phase =>
                phase.sectionId === sectionId
                    ? {
                          ...phase,
                          assignedUserIds: (phase.assignedUserIds || []).filter(
                              id => id !== userId,
                          ),
                      }
                    : phase,
            ),
        )
        setDirty(true)
    }

    const openAddModal = (phase, mode) => {
        setModalType('recipient')
        setModalSection(phase)
        setRecMode(mode)
        setUserSearch('')
        setUserMatches(companyUsers)
        setPendingUserId(null)
        setPendingUserIds([])
        setExternalName('')
        setExternalEmail('')
        setModalOpen(true)
    }

    const openAssignedUserModal = phase => {
        setModalType('assigned')
        setModalSection(phase)
        setRecMode('internal')
        setUserSearch('')
        setUserMatches(companyUsers)
        setPendingUserId(null)
        setPendingUserIds([...(phase.assignedUserIds || [])])
        setExternalName('')
        setExternalEmail('')
        setModalOpen(true)
    }

    const closeAddModal = () => {
        setModalOpen(false)
        setModalSection(null)
        setModalType('recipient')
        setPendingUserId(null)
        setPendingUserIds([])
    }

    const togglePendingAssignedUser = userId => {
        setPendingUserIds(prev =>
            prev.includes(userId)
                ? prev.filter(id => id !== userId)
                : [...prev, userId],
        )
    }

    useEffect(() => {
        if (!modalOpen) return undefined
        if (modalType === 'recipient' && recMode !== 'internal') {
            return undefined
        }

        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current)
        }

        const debounceMs = userSearch.trim() ? 300 : 0
        searchTimerRef.current = setTimeout(async () => {
            setUserSearchLoading(true)
            try {
                const rows = await SectionWorkflowService.searchUsers({
                    q: userSearch.trim(),
                    companyId,
                    limit: USER_SEARCH_DEFAULT_LIMIT,
                })
                setUserMatches(
                    normalizeUserSearchResults(
                        unwrapUserSearchResponse(rows),
                    ),
                )
                if (modalType === 'recipient') {
                    setPendingUserId(null)
                }
            } catch (err) {
                setUserMatches([])
                errorToastRef.current(extractWorkflowErrorMessage(err))
            } finally {
                setUserSearchLoading(false)
            }
        }, debounceMs)

        return () => {
            if (searchTimerRef.current) {
                clearTimeout(searchTimerRef.current)
            }
        }
    }, [modalOpen, modalType, recMode, userSearch, companyId])

    const handleConfirmAdd = () => {
        if (!modalSection?.sectionId) return
        const { sectionId } = modalSection

        if (modalType === 'assigned') {
            if (!pendingUserIds.length) {
                errorToast('Pick at least one user.')
                return
            }
            const selectedUsers = dedupeCompanyUsers(
                pendingUserIds
                    .map(
                        userId =>
                            userMatches.find(user => user.id === userId) ||
                            companyUsers.find(user => user.id === userId) ||
                            (modalSection.assignedUsers || []).find(
                                user => user.id === userId,
                            ),
                    )
                    .filter(Boolean),
            )
            if (selectedUsers.length) {
                setCompanyUsers(prev =>
                    dedupeCompanyUsers([...prev, ...selectedUsers]),
                )
                pinnedAssignedUsersRef.current = dedupeCompanyUsers([
                    ...pinnedAssignedUsersRef.current,
                    ...selectedUsers,
                ])
            }
            updatePhase(sectionId, {
                assignedUserIds: [...pendingUserIds],
                assignedUsers: selectedUsers,
            })
            closeAddModal()
            successToast('Assigned users updated.')
            return
        }

        const current = recipientsBySection[sectionId] || []
        let candidate = null

        if (recMode === 'internal') {
            if (!pendingUserId) {
                errorToast('Pick a user from the list.')
                return
            }
            const match = userMatches.find(u => u.id === pendingUserId)
            candidate = normalizeNotificationRecipient({
                InternalUserId: pendingUserId,
                InternalUserName: match?.fullName || '',
                InternalUserEmail: match?.email || '',
            })
            if (match) {
                setCompanyUsers(prev =>
                    dedupeCompanyUsers([...prev, match]),
                )
            }
        } else {
            const email = externalEmail.trim()
            const name = externalName.trim()
            if (!email) {
                errorToast('Email is required.')
                return
            }
            candidate = normalizeNotificationRecipient({
                ExternalEmail: email,
                ExternalName: name,
            })
        }

        if (isDuplicateRecipient(current, candidate)) {
            errorToast('That recipient is already on this list.')
            return
        }

        const nextKey = `new-${recipientKeyRef.current}`
        recipientKeyRef.current += 1
        setRecipientsBySection(prev => ({
            ...prev,
            [sectionId]: [...current, { ...candidate, key: nextKey }],
        }))
        setDirty(true)
        closeAddModal()
        successToast('Recipient added.')
    }

    const handleReset = () => {
        // eslint-disable-next-line no-alert
        if (dirty && !window.confirm('Discard unsaved changes?')) return
        setPhases(clonePhases(savedPhases))
        setRecipientsBySection(cloneRecipientsMap(savedRecipientsBySection))
        setDirty(false)
        successToast('Reverted to last saved values.')
    }

    const handleSaveAll = async () => {
        const validationError = validatePhaseSetup(phases)
        if (validationError) {
            errorToast(validationError)
            return
        }

        setSaving(true)
        try {
            const assignments = [...phases]
                .filter(phase => phase.sectionId)
                .sort((a, b) => a.phaseOrder - b.phaseOrder)
                .map(toWorkflowAssignmentPayload)

            await SectionWorkflowService.saveWorkflow(
                SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID,
                assignments,
            )

            await Promise.all(
                phases
                    .filter(phase => phase.sectionId)
                    .map(async phase => {
                    const recipients = recipientsBySection[phase.sectionId] || []
                    await SectionWorkflowService.saveNotificationRecipients(
                        SPECIAL_CUSTOMER_CLAIM_TEMPLATE_ID,
                        phase.sectionId,
                        recipients.map(toNotificationRecipientPayload),
                    )
                }),
            )

            setSavedPhases(clonePhases(phases))
            setSavedRecipientsBySection(
                cloneRecipientsMap(recipientsBySection),
            )
            setDirty(false)
            successToast(
                'Phase Setup saved — workflow + recipients applied to all phases.',
            )
        } catch (err) {
            errorToast(extractWorkflowErrorMessage(err))
        } finally {
            setSaving(false)
        }
    }

    if (loading) {
        return (
            <PageWrap>
                <Content>
                    <LoaderBar content="Loading phase setup" />
                </Content>
            </PageWrap>
        )
    }

    return (
        <PageWrap>
            <Content>
                <Crumb>
                    <span>{PORTAL_SECTION_NAME}</span> ›{' '}
                    <span>Phase Setup</span> ·{' '}
                    <span className="small">Super Admin</span>
                </Crumb>
                <PageTitle>Phase Setup</PageTitle>
                <PageSub>
                    Who gets emailed when each section is completed, uncompleted,
                    or edited. Internal users (resolved to their account email at
                    send time) and external email addresses (vendors, the customer
                    themselves) are both supported.
                </PageSub>

                {accessDenied ? (
                    <Message warning>
                        Super Admin only. Section workflow configuration affects
                        every audit on the template.
                    </Message>
                ) : null}

                {!accessDenied && !phases.length ? (
                    <Message info>
                        No workflow phases are configured yet. Save Workflow Setup
                        first, then return here to configure phases.
                    </Message>
                ) : null}

                {!accessDenied &&
                    sortedPhases.map(phase => {
                        const recipients =
                            recipientsBySection[phase.sectionId] || []
                        const assignedUsers = resolveAssignedUsersForPhase(
                            phase,
                            companyUsers,
                        )
                        return (
                            <PhaseCard key={phase.sectionId}>
                                <SimplePhaseHeader>
                                    Phase {phase.phaseOrder} · {phase.sectionName}
                                </SimplePhaseHeader>
                                <CardBody>
                                    <PhaseHint>
                                        These addresses get the{' '}
                                        <strong>
                                            {phase.completeLabel || 'Complete'}
                                        </strong>{' '}
                                        → next phase notification. (Final phase +
                                        Uncomplete + Edit fire a deduped
                                        broadcast across every phase&apos;s list.)
                                    </PhaseHint>
                                    <AssignedUserField>
                                        <Form>
                                            <Form.Field required>
                                                <label style={{ fontWeight: 700 }}>
                                                    Assigned User
                                                </label>
                                                {assignedUsers.length > 0 ? (
                                                    <AssignedChipArea>
                                                        {assignedUsers.map(
                                                            user =>
                                                                renderAssignedUserChip(
                                                                    user,
                                                                    userId =>
                                                                        handleRemoveAssignedUser(
                                                                            phase.sectionId,
                                                                            userId,
                                                                        ),
                                                                    saving,
                                                                ),
                                                        )}
                                                    </AssignedChipArea>
                                                ) : null}
                                                <AssignedActionRow>
                                                    <AppPrimaryButton
                                                        type="button"
                                                        size="small"
                                                        disabled={saving}
                                                        onClick={() =>
                                                            openAssignedUserModal(
                                                                phase,
                                                            )
                                                        }
                                                    >
                                                        + Add assigned user
                                                    </AppPrimaryButton>
                                                </AssignedActionRow>
                                                <HintText>
                                                    Only these users can click
                                                    Complete on this phase.
                                                    Required.
                                                </HintText>
                                            </Form.Field>
                                        </Form>
                                    </AssignedUserField>
                                </CardBody>

                                <RecipientSection>
                                    <Form>
                                        <Form.Field>
                                            <label style={{ fontWeight: 700 }}>
                                                Email subject template
                                            </label>
                                            <Form.Input
                                                fluid
                                                value={phase.emailSubject}
                                                disabled={saving}
                                                onChange={(_, { value }) =>
                                                    updatePhase(
                                                        phase.sectionId,
                                                        {
                                                            emailSubject: value,
                                                        },
                                                    )
                                                }
                                            />
                                            <SubjectHint>
                                                Supports{' '}
                                                <code>
                                                    {'{{CLAIM_NUMBER}}'}
                                                </code>
                                                ,{' '}
                                                <code>
                                                    {'{{CUSTOMER_NAME}}'}
                                                </code>
                                                ,{' '}
                                                <code>
                                                    {'{{COMPLETED_BY}}'}
                                                </code>
                                            </SubjectHint>
                                            {recipients.length > 0 ? (
                                                <RecipientChipArea>
                                                    {recipients.map(
                                                        recipient =>
                                                            renderRecipientChip(
                                                                recipient,
                                                                key =>
                                                                    handleRemoveRecipient(
                                                                        phase.sectionId,
                                                                        key,
                                                                    ),
                                                                saving,
                                                            ),
                                                    )}
                                                </RecipientChipArea>
                                            ) : null}
                                        </Form.Field>
                                    </Form>
                                    <RecipientActionRow
                                        $hasRecipients={recipients.length > 0}
                                    >
                                        <AppPrimaryButton
                                            type="button"
                                            size="small"
                                            disabled={saving}
                                            onClick={() =>
                                                openAddModal(phase, 'internal')
                                            }
                                        >
                                            + Add internal user
                                        </AppPrimaryButton>
                                        <AppSecondaryButton
                                            type="button"
                                            size="small"
                                            disabled={saving}
                                            onClick={() =>
                                                openAddModal(phase, 'external')
                                            }
                                        >
                                            + Add external email
                                        </AppSecondaryButton>
                                    </RecipientActionRow>
                                </RecipientSection>
                            </PhaseCard>
                        )
                    })}

                {!accessDenied && phases.length > 0 ? (
                    <SaveBar>
                        <SaveBarMsg>
                            {dirty ? (
                                <>
                                    <DirtyDot />
                                    Unsaved changes — click{' '}
                                    <strong>Save All Phases</strong> to apply.
                                </>
                            ) : (
                                'All saved.'
                            )}
                        </SaveBarMsg>
                        <SaveBarActions>
                            <AppSecondaryButton
                                type="button"
                                disabled={saving}
                                onClick={handleReset}
                            >
                                Reset
                            </AppSecondaryButton>
                            <AppPrimaryButton
                                type="button"
                                loading={saving}
                                disabled={saving}
                                onClick={handleSaveAll}
                            >
                                Save All Phases
                            </AppPrimaryButton>
                        </SaveBarActions>
                    </SaveBar>
                ) : null}

                <Modal
                    open={modalOpen}
                    onClose={closeAddModal}
                    size="small"
                    className="model"
                >
                    <Modal.Header>
                        <FlexRowBetween>
                            <TitleText>
                                {modalType === 'assigned'
                                    ? 'Add assigned user to '
                                    : 'Add recipient to '}
                                {modalSection?.sectionName || 'section'}
                            </TitleText>
                            <X
                                className="pointer"
                                size={32}
                                onClick={closeAddModal}
                            />
                        </FlexRowBetween>
                    </Modal.Header>
                    <Modal.Content>
                        <Form noValidate>
                            {modalType === 'recipient' ? (
                                <ModalModeRow>
                                    {recMode === 'internal' ? (
                                        <AppPrimaryButton
                                            type="button"
                                            onClick={() =>
                                                setRecMode('internal')
                                            }
                                        >
                                            <User size={16} />
                                            Internal user
                                        </AppPrimaryButton>
                                    ) : (
                                        <AppSecondaryButton
                                            type="button"
                                            onClick={() =>
                                                setRecMode('internal')
                                            }
                                        >
                                            <User size={16} />
                                            Internal user
                                        </AppSecondaryButton>
                                    )}
                                    {recMode === 'external' ? (
                                        <AppPrimaryButton
                                            type="button"
                                            onClick={() =>
                                                setRecMode('external')
                                            }
                                        >
                                            <EnvelopeSimple size={16} />
                                            External email
                                        </AppPrimaryButton>
                                    ) : (
                                        <AppSecondaryButton
                                            type="button"
                                            onClick={() =>
                                                setRecMode('external')
                                            }
                                        >
                                            <EnvelopeSimple size={16} />
                                            External email
                                        </AppSecondaryButton>
                                    )}
                                </ModalModeRow>
                            ) : null}

                            {modalType === 'assigned' ||
                            recMode === 'internal' ? (
                                <>
                                    <Form.Field>
                                        <label style={{ fontWeight: 700 }}>
                                            Search by name or email
                                        </label>
                                        <Form.Input
                                            fluid
                                            placeholder="Type to search…"
                                            value={userSearch}
                                            onChange={(_, { value }) =>
                                                setUserSearch(value)
                                            }
                                        />
                                    </Form.Field>
                                    <SearchStatusRow>
                                        {userSearchLoading ? (
                                            <Loader active inline size="mini" />
                                        ) : null}
                                        {!userSearchLoading &&
                                        userMatches.length === 0 ? (
                                            <span>No matches.</span>
                                        ) : null}
                                    </SearchStatusRow>
                                    <UserMatchList>
                                        {!userSearchLoading &&
                                            userMatches.map(match => {
                                                const isSelected =
                                                    modalType === 'assigned'
                                                        ? pendingUserIds.includes(
                                                              match.id,
                                                          )
                                                        : pendingUserId ===
                                                          match.id
                                                return (
                                                    <UserMatchItem
                                                        key={match.id}
                                                        type="button"
                                                        className={
                                                            isSelected
                                                                ? 'selected'
                                                                : ''
                                                        }
                                                        onClick={() =>
                                                            modalType ===
                                                            'assigned'
                                                                ? togglePendingAssignedUser(
                                                                      match.id,
                                                                  )
                                                                : setPendingUserId(
                                                                      match.id,
                                                                  )
                                                        }
                                                    >
                                                        <User
                                                            size={14}
                                                            weight="bold"
                                                        />
                                                        {match.fullName}
                                                        {match.email ? (
                                                            <span className="chip-meta">
                                                                {' '}
                                                                · {match.email}
                                                            </span>
                                                        ) : null}
                                                        {isSelected ? (
                                                            <Check
                                                                className="chip-selected"
                                                                size={14}
                                                                weight="bold"
                                                            />
                                                        ) : null}
                                                    </UserMatchItem>
                                                )
                                            })}
                                    </UserMatchList>
                                </>
                            ) : (
                                <>
                                    <Form.Field>
                                        <label style={{ fontWeight: 700 }}>
                                            Name
                                        </label>
                                        <Form.Input
                                            fluid
                                            placeholder="ACME QA Lead"
                                            value={externalName}
                                            onChange={(_, { value }) =>
                                                setExternalName(value)
                                            }
                                        />
                                    </Form.Field>
                                    <Form.Field>
                                        <label style={{ fontWeight: 700 }}>
                                            Email
                                        </label>
                                        <Form.Input
                                            fluid
                                            type="email"
                                            placeholder="qa@acme.example.com"
                                            value={externalEmail}
                                            onChange={(_, { value }) =>
                                                setExternalEmail(value)
                                            }
                                        />
                                    </Form.Field>
                                </>
                            )}
                        </Form>
                    </Modal.Content>
                    <Modal.Actions>
                        <AppSecondaryButton
                            type="button"
                            onClick={closeAddModal}
                        >
                            Cancel
                        </AppSecondaryButton>
                        <AppPrimaryButton type="button" onClick={handleConfirmAdd}>
                            {modalType === 'assigned' ? 'Save' : 'Add'}
                        </AppPrimaryButton>
                    </Modal.Actions>
                </Modal>
            </Content>
        </PageWrap>
    )
}
